import * as React from 'react';

import { QuestionTypes } from '@showbie-socrative/socrative-utils';
import cx from 'classnames';

import { QuestionViewEventAdapter } from '~analytics/adapters/QuestionViewEventAdapter';
import { sendQuestionViewClickEvent } from '~analytics/index';
import { translate } from '~l10n/client';
import { FocusIndicator } from '~shared/components/FocusIndicator/FocusIndicator';
import { ActivityResponse } from '~teacher/hook';
import { queryClient } from '~teacher/queryClient';

import { ResultsQuestionTypeIcon } from './ResultsQuestionTypeIcon';

interface CellProps {
  questionType: QuestionTypes;
  order: number;
  activityInstanceId: number;
}

const questionBackgroundColorMap: { [key in QuestionTypes]: string } = {
  [QuestionTypes.MultipleChoice]: 'bg-amber-500 group-hocus:bg-amber-600',
  [QuestionTypes.TrueFalse]: 'bg-indigo-600 group-hocus:bg-indigo-700',
  [QuestionTypes.FreeResponse]: 'bg-orange-600 group-hocus:bg-orange-700',
  [QuestionTypes.Matching]: '',
};

const questionHeaderBackgroundColorMap: { [key in QuestionTypes]: string } = {
  [QuestionTypes.MultipleChoice]:
    'bg-amber-50 border-amber-500 ' +
    'hover:border-amber-600 focus-within:border-amber-600 ' +
    'hover:bg-amber-100 focus-within:bg-amber-100',

  [QuestionTypes.TrueFalse]:
    'bg-indigo-50 border-indigo-600 ' +
    'hover:border-indigo-700 focus-within:border-indigo-700 ' +
    'hover:bg-indigo-100 focus-within:bg-indigo-100',

  [QuestionTypes.FreeResponse]:
    'bg-orange-50 border-orange-600 ' +
    'hover:border-orange-700 focus-within:border-orange-700 ' +
    'hover:bg-orange-100 focus-within:bg-orange-100',

  [QuestionTypes.Matching]: '',
};

const questionTextColorMap: { [key in QuestionTypes]: string } = {
  [QuestionTypes.MultipleChoice]: 'text-amber-900',
  [QuestionTypes.TrueFalse]: 'text-indigo-900',
  [QuestionTypes.FreeResponse]: 'text-orange-900',
  [QuestionTypes.Matching]: '',
}

/**
 * Displays a table header cell for a question in an activity. Styled
 * based on the question type.
 */
export function ResultsQuestionCell({
  questionType,
  order,
  activityInstanceId,
}: CellProps): React.ReactElement {
  /**
   * NOTE: Somewhat brittle route variable here, but trying to keep route
   *       changes limited to anchor elements where possible. #UseThePlatform
   */
  const routeToQuestion = `#results/${activityInstanceId}/questions/${order}`;
  const activityData = queryClient.getQueryData([
    'activity',
    activityInstanceId,
  ]) as ActivityResponse | undefined;
  const activity = activityData?.activity;

  const indicatorClass = cx(
    'absolute right-0 bottom-0 left-0 h-12 max-h-4px rounded-1',
    questionBackgroundColorMap[questionType]
  );

  const questionHeaderClass = cx(
    'group relative min-w-24 h-full py-2 text-center rounded-4px border border-solid',
    questionHeaderBackgroundColorMap[questionType]
  );

  return (
    <div className={questionHeaderClass}>
      <span className={cx("flex items-center justify-center gap-1 relative z-10 text-sm font-500 leading-8 pointer-events-none", questionTextColorMap[questionType])}>
        <ResultsQuestionTypeIcon questionType={questionType} /> {order}
      </span>
      <a
        className="absolute inset-0 rounded-4px outline-none"
        href={routeToQuestion}
        onClick={() => {
          // Analytics
          if (activity) {
            sendQuestionViewClickEvent({
              activity_state: QuestionViewEventAdapter.getActivityStateForEvent(
                activity.state
              ),
              question_type: questionType,
            });
          }
        }}
      >
        <span className="sr-only">{translate('View question results')}</span>
        <div className={indicatorClass} />
        <FocusIndicator notOffset isInset />
      </a>
    </div>
  );
}
